import { StyleSheet } from 'react-native';
import type { Theme } from '../../../src/theme/getTheme';
import { makeBoxShadow } from '../../../src/shared/helpers/shadowStyles.helper';

/** Rank crest on the 100pt VS ring (specs/rank-on-social-screens AC-8, design 1b). */
export const H2H_CREST_SIZE = 56;

export const getFriendStatsHeaderStyles = (theme: Theme) =>
  StyleSheet.create({
    vsSection: {
      flexDirection: 'row',
      alignItems: 'flex-start',
      justifyContent: 'center',
      paddingVertical: 20,
      paddingHorizontal: 16,
      backgroundColor: 'transparent',
    },
    vsAvatarColumn: {
      alignItems: 'center',
      flex: 1,
      minWidth: 0,
    },
    vsAvatarRingUser: {
      width: 100,
      height: 100,
      borderRadius: 50,
      borderWidth: 3,
      borderColor: theme.info,
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: theme.background.card,
      ...makeBoxShadow({ shadowColor: theme.info, shadowOffset: { width: 0, height: 2 }, shadowOpacity: 0.25, shadowRadius: 8 }),
      elevation: 4,
    },
    vsAvatarRingFriend: {
      width: 100,
      height: 100,
      borderRadius: 50,
      borderWidth: 3,
      borderColor: theme.error,
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: theme.background.card,
      ...makeBoxShadow({ shadowColor: theme.error, shadowOffset: { width: 0, height: 2 }, shadowOpacity: 0.25, shadowRadius: 8 }),
      elevation: 4,
    },
    vsAvatarInner: {
      width: 88,
      height: 88,
      borderRadius: 44,
    },
    /** Neutral placeholder while user avatar loads (no initials to avoid double visual). */
    vsAvatarPlaceholder: {
      width: 88,
      height: 88,
      borderRadius: 44,
      backgroundColor: theme.gray[300],
    },
    vsPlayerName: {
      fontSize: 14,
      fontWeight: '700',
      color: theme.gray[900],
      marginTop: 10,
      textAlign: 'center',
      maxWidth: 120,
    },
    vsTextCenter: {
      alignItems: 'center',
      justifyContent: 'center',
      paddingHorizontal: 4,
      paddingTop: 28,
      width: 80,
      flexShrink: 0,
      backgroundColor: 'transparent',
    },
    vsLottie: {
      width: 80,
      height: 80,
      backgroundColor: 'transparent',
    },
    vsTextLabel: {
      fontSize: 26,
      fontWeight: '700',
      color: theme.primary,
      opacity: 0.9,
      letterSpacing: 2,
    },
    // Bottom-right of the ring; the crown owns the top-right and is untouched (AC-8).
    vsRankCrest: {
      position: 'absolute',
      right: -10,
      bottom: -6,
      zIndex: 10,
    },
    // In the column's existing gap under the name — the section does not grow (spec §7).
    vsRankLevelPill: {
      alignSelf: 'center',
    },
    vsCrownBadge: {
      position: 'absolute',
      top: -6,
      right: -2,
      zIndex: 10,
      backgroundColor: theme.background.card,
      borderRadius: 14,
      width: 28,
      height: 28,
      alignItems: 'center',
      justifyContent: 'center',
      ...makeBoxShadow({ shadowColor: theme.warning, shadowOffset: { width: 0, height: 2 }, shadowOpacity: 0.4, shadowRadius: 4 }),
      elevation: 6,
    },
  });

const VS_LOTTIE_MIN = 64;
const VS_LOTTIE_MAX = 120;

export const getVsLottieSize = (windowWidth: number) =>
  Math.min(VS_LOTTIE_MAX, Math.max(VS_LOTTIE_MIN, Math.round(windowWidth * 0.15)));

export const getVsCenterDynamicStyles = (size: number) =>
  StyleSheet.create({
    vsTextCenter: {
      width: size,
    },
    vsLottie: {
      width: size,
      height: size,
      backgroundColor: 'transparent',
    },
  });
